import React from "react";
import { graphql } from "gatsby";

import {
    measurePage,
    pageShell,
    hero,
    heroCopy,
    breadcrumbs,
    heroTitle,
    heroLead,
    heroTips,
    heroTip,
    heroTipIcon,
    heroVisual,
    section,
    sectionHeading,
    sectionAccent,
    measurementsGrid,
    measurementCard,
    measurementIcon,
    measurementNumber,
    measurementTitle,
    measurementText,
    clothesSection,
    clothesIntro,
    checklist,
    checklistItem,
    clothingGrid,
    clothingCard,
    clothingIcon,
    mistakesSection,
    mistakesInner,
    mistakesIntro,
    mistakesGrid,
    mistakeCard,
    mistakeIcon,
    faqGrid,
    faqCard,
    faqTitle,
    faqPlus,
    faqAnswer,
    reminder,
    reminderCopy,
    reminderEyebrow,
    reminderImage,
    reminderAction,
} from "./how-to-measure.module.scss";

import bodyNeckImage from "../../assets/images/measure/guide/body-v2-01-neck.png";
import bodyChestImage from "../../assets/images/measure/guide/body-v2-02-chest.png";
import bodySleeveImage from "../../assets/images/measure/guide/body-v2-03-sleeve.png";
import bodyTorsoImage from "../../assets/images/measure/guide/body-v2-04-torso.png";
import bodyBackLengthImage from "../../assets/images/measure/guide/body-v2-05-back-length.png";
import bodyBackWidthImage from "../../assets/images/measure/guide/body-v2-06-back-width.png";
import bodyWaistImage from "../../assets/images/measure/guide/body-v2-07-waist.png";
import bodyHipsImage from "../../assets/images/measure/guide/body-v2-08-hips.png";
import bodyRiseImage from "../../assets/images/measure/guide/body-v2-09-rise.png";
import bodyBeltImage from "../../assets/images/measure/guide/body-v2-10-belt.png";
import clothingWaistImage from "../../assets/images/measure/guide/clothing-v2-01-pants-waist.png";
import clothingTotalLengthImage from "../../assets/images/measure/guide/clothing-v2-02-pants-total-length.png";
import clothingInseamImage from "../../assets/images/measure/guide/clothing-v2-03-pants-inseam.png";
import clothingChestImage from "../../assets/images/measure/guide/clothing-v2-04-top-chest.png";
import clothingShouldersImage from "../../assets/images/measure/guide/clothing-v2-05-top-shoulders.png";
import clothingSleeveImage from "../../assets/images/measure/guide/clothing-v2-06-top-sleeve.png";
import thinClothingRuleImage from "../../assets/images/measure/hero-rules/rule-01-thin-clothing.png";
import tapeFitRuleImage from "../../assets/images/measure/hero-rules/rule-02-tape-fit.png";
import standStraightRuleImage from "../../assets/images/measure/hero-rules/rule-03-stand-straight.png";
import helperRuleImage from "../../assets/images/measure/hero-rules/rule-04-helper.png";
import naturalBreathRuleImage from "../../assets/images/measure/hero-rules/rule-05-natural-breath.png";

import heroImage from "../../assets/images/measure/reference/measure-hero-reference.jpg";
import tapeImage from "../../assets/images/measure/reference/measure-tape-reference.jpg";
import notebookImage from "../../assets/images/measure/reference/measure-notebook-reference.jpg";

import usePageMetadata from "../../hooks/use-page-metadata";
import { IPageBase } from "../../models/page-base.model";
import { ISite } from "../../models/site.model";
import MainLayout from "../../layouts/main-layout";
import StaticPageNav from "../organisms/static-page-nav";
import MinimalFooter from "../organisms/minimal-footer";

interface IMeasureProps extends IPageBase {
    readonly data: { site: ISite };
}

const bodyMeasurements = [
    { image: bodyNeckImage, title: "Obwód szyi", text: "Zmierz wokół nasady szyi, na wysokości kości obojczyka." },
    { image: bodyChestImage, title: "Obwód klatki piersiowej", text: "Zmierz w najszerszym miejscu biustu, prowadząc miarkę przez środek pleców." },
    { image: bodySleeveImage, title: "Długość rękawa", text: "Zmierz od szczytu ramienia do nadgarstka przy lekko ugiętej ręce." },
    { image: bodyTorsoImage, title: "Długość tułowia", text: "Zmierz od najwyższego punktu ramienia do talii." },
    { image: bodyBackLengthImage, title: "Długość pleców", text: "Zmierz od karku pionowo do talii." },
    { image: bodyBackWidthImage, title: "Szerokość pleców", text: "Zmierz od barku do barku na wysokości łopatek." },
    { image: bodyWaistImage, title: "Obwód talii", text: "Zmierz w najwęższym miejscu talii, nie napinając mięśni." },
    { image: bodyHipsImage, title: "Obwód bioder", text: "Zmierz w najszerszej części bioder i pośladków." },
    { image: bodyRiseImage, title: "Wysokość stanu", text: "Zmierz od kroku do miejsca, gdzie znajduje się pas spodni." },
    { image: bodyBeltImage, title: "Obwód pasa", text: "Zmierz na wysokości pępka — tam, gdzie zazwyczaj kończą się spodnie." },
];

const clothingMeasurements = [
    { image: clothingWaistImage, title: "Szerokość pasa" },
    { image: clothingTotalLengthImage, title: "Długość całkowita" },
    { image: clothingInseamImage, title: "Długość nogawki" },
    { image: clothingChestImage, title: "Szerokość klatki piersiowej" },
    { image: clothingShouldersImage, title: "Szerokość barków" },
    { image: clothingSleeveImage, title: "Długość rękawa" },
];

const heroRules = [
    { image: thinClothingRuleImage, text: "Mierz się w cienkiej bieliźnie lub dopasowanym stroju" },
    { image: tapeFitRuleImage, text: "Centymetr powinien przylegać do ciała, ale go nie uciskać" },
    { image: standStraightRuleImage, text: "Stań prosto i swobodnie" },
    { image: helperRuleImage, text: "Najlepiej poproś drugą osobę o pomoc" },
    { image: naturalBreathRuleImage, text: "Nie wciągaj brzucha" },
];

const preparation = [
    "Zapnij wszystkie guziki i zamki",
    "Rozłóż ubranie na płaskiej powierzchni",
    "Wygładź materiał",
    "Nie rozciągaj tkaniny podczas mierzenia",
];

const mistakes = [
    "Wciąganie brzucha",
    "Zbyt mocno zaciśnięty centymetr",
    "Mierzenie w grubych ubraniach",
    "Przekrzywiony centymetr",
    "Pomiar wykonywany w pośpiechu",
];

const faq = [
    { q: "Czy mogę zmierzyć się samodzielnie?", a: "Tak, jednak pomiary pleców, rękawów czy długości najlepiej wykonać z pomocą drugiej osoby." },
    { q: "Czy każda marka ma takie same rozmiary?", a: "Nie. Dlatego zawsze warto porównywać swoje wymiary z tabelą producenta." },
    { q: "Jak często warto aktualizować wymiary?", a: "Jeżeli Twoja sylwetka się zmienia lub regularnie ćwiczysz — najlepiej co 2–3 miesiące." },
    { q: "Na wdechu czy na wydechu mierzyć wymiary?", a: "Mierz w naturalnym oddechu. Nie wciągaj brzucha ani nie wypinaj klatki piersiowej." },
    { q: "Co zrobić, jeśli wypadam między dwoma rozmiarami?", a: "Porównaj tabelę rozmiarów konkretnej marki. Przy luźniejszych fasonach wybierz mniejszy rozmiar, a przy dopasowanych — większy." },
    { q: "Czy centymetr powinien być luźny?", a: "Centymetr powinien przylegać do ciała, ale nie uciskać. Powinien móc swobodnie się przesuwać." },
];

const HowToMeasure: React.FC<IMeasureProps> = ({ data, pageContext }) => {
    const { pageTitle, defaultStructuredData } = usePageMetadata(pageContext, data.site);

    return (
        <MainLayout
            className={measurePage}
            HeaderComponent={null}
            footer={false}
            SEOProps={{ title: pageTitle, structuredData: defaultStructuredData }}
        >
            <StaticPageNav menuId="measure-mobile-menu" />
            <main className={pageShell}>
                <section className={hero} aria-labelledby="measure-title">
                    <div className={heroCopy}>
                        <nav className={breadcrumbs} aria-label="Okruszki">
                            <span>STRONA GŁÓWNA</span><span>/</span><span>JAK TO DZIAŁA</span><span>/</span><span>JAK PRAWIDŁOWO SIĘ MIERZYĆ</span>
                        </nav>
                        <h1 className={heroTitle} id="measure-title">Jak prawidłowo<br /><em>się mierzyć?</em></h1>
                        <p className={heroLead}>Dokładne wymiary to sekret udanych<br />zakupów online.</p>
                        <ul className={heroTips} aria-label="Najważniejsze zasady pomiaru">
                            {heroRules.map((rule) => (
                                <li className={heroTip} key={rule.text}>
                                    <img className={heroTipIcon} src={rule.image} alt="" aria-hidden="true" />
                                    <span>{rule.text}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className={heroVisual}>
                        <img src={heroImage} alt="Kobieta mierząca talię centymetrem krawieckim" />
                    </div>
                </section>

                <section className={section} aria-labelledby="body-measurements-title">
                    <header className={sectionHeading}>
                        <h2 id="body-measurements-title">Pomiary sylwetki</h2>
                        <span className={sectionAccent} aria-hidden="true" />
                    </header>
                    <div className={measurementsGrid}>
                        {bodyMeasurements.map(({ image, title, text }, index) => (
                            <article className={measurementCard} key={title}>
                                <div className={measurementIcon}><img src={image} alt="" aria-hidden="true" /></div>
                                <span className={measurementNumber}>{String(index + 1).padStart(2, "0")}</span>
                                <h3 className={measurementTitle}>{title}</h3>
                                <p className={measurementText}>{text}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className={clothesSection} aria-labelledby="clothes-title">
                    <div className={clothesIntro}>
                        <h2>Jak prawidłowo mierzyć ubrania?</h2>
                        <ul className={checklist}>
                            {preparation.map((item) => <li className={checklistItem} key={item}><span aria-hidden="true">✓</span>{item}</li>)}
                        </ul>
                    </div>
                    <div>
                        <header className={sectionHeading}><h2 id="clothes-title">Pomiary odzieży</h2><span className={sectionAccent} aria-hidden="true" /></header>
                        <div className={clothingGrid}>
                            {clothingMeasurements.map(({ image, title }) => (
                                <article className={clothingCard} key={title}>
                                    <div className={clothingIcon}><img src={image} alt="" aria-hidden="true" /></div>
                                    <h3>{title}</h3>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className={mistakesSection} aria-labelledby="mistakes-title">
                    <div className={mistakesInner}>
                        <div className={mistakesIntro}>
                            <h2 id="mistakes-title">Najczęstsze błędy</h2>
                            <div className={mistakesGrid}>
                                {mistakes.map((item) => (
                                    <article className={mistakeCard} key={item}>
                                        <span className={mistakeIcon} aria-hidden="true">×</span>
                                        <p>{item}</p>
                                    </article>
                                ))}
                            </div>
                        </div>
                        <img src={tapeImage} alt="Zwinięty centymetr krawiecki" />
                    </div>
                </section>

                <section className={section} aria-labelledby="faq-title">
                    <header className={sectionHeading}><h2 id="faq-title">FAQ – najczęściej zadawane pytania</h2><span className={sectionAccent} aria-hidden="true" /></header>
                    <div className={faqGrid}>
                        {faq.map(({ q, a }) => (
                            <article className={faqCard} key={q}>
                                <div className={faqTitle}><h3>{q}</h3><span className={faqPlus} aria-hidden="true">+</span></div>
                                <p className={faqAnswer}>{a}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className={reminder} aria-labelledby="reminder-title">
                    <div className={reminderCopy}>
                        <p className={reminderEyebrow}>♡ &nbsp; Pamiętaj!</p>
                        <h2 id="reminder-title">Dokładne pomiary to najlepszy sposób na udane zakupy online.</h2>
                        <p>Kilka minut poświęconych na zmierzenie sylwetki pozwoli uniknąć zwrotów i sprawi, że ubrania będą wyglądały dokładnie tak, jak powinny.</p>
                    </div>
                    <div className={reminderImage}><img src={notebookImage} alt="Notatnik z zapisanymi wymiarami i centymetr krawiecki" /></div>
                    <div className={reminderAction}>Zapisz swoje wymiary<br />i korzystaj przy<br />każdych zakupach! <span aria-hidden="true">♡</span></div>
                </section>
            </main>
            <MinimalFooter />
        </MainLayout>
    );
};

export const query = graphql`
    query($language: String!) {
        locales: allLocale(filter: { language: { eq: $language } }) {
            edges { node { ns data language } }
        }
        site { ...siteFields }
    }
`;

export default HowToMeasure;
